import { c as _c } from "react/compiler-runtime";
import { forwardRef, type ComponentProps, type ComponentType, type CSSProperties, type ForwardedRef, type ReactElement, type ReactNode } from 'react';
import type { Color } from '../types';
import styles from './Banner.module.css';
import { Icon, type IconName } from '../Icon';
import classNames from 'classnames';
type BaseBannerProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};
type ColorProps = {
  color: Color;
} | {
  htmlColor: string;
};
type BannerProps = BaseBannerProps & ColorProps;
function getColorStyle(props: ColorProps) {
  let colorVarValue: string = 'var(--betterplace-color-accent-secondary)';
  if ('color' in props && props.color) {
    colorVarValue = `var(--betterplace-color-${props.color})`;
  } else if ('htmlColor' in props && props.htmlColor) {
    colorVarValue = props.htmlColor;
  }
  return {
    '--banner-color': colorVarValue
  } as CSSProperties;
}
const Banner = forwardRef(function Banner(t0, ref) {
  const $ = _c(11);
  let children;
  let style;
  let t1;
  if ($[0] !== t0) {
    const {
      children: t2,
      className,
      style: t3,
      ...props
    } = t0;
    children = t2;
    style = t3;
    t1 = getColorStyle(props);
    $[0] = t0;
    $[1] = children;
    $[2] = style;
    $[3] = t1;
  } else {
    children = $[1];
    style = $[2];
    t1 = $[3];
  }
  const colorStyle = t1;
  let t2;
  if ($[4] !== colorStyle || $[5] !== style) {
    t2 = {
      ...style,
      ...colorStyle
    };
    $[4] = colorStyle;
    $[5] = style;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== ref || $[9] !== t2) {
    t3 = <div ref={ref} className={styles.container} style={t2}>{children}</div>;
    $[7] = children;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RestrictChildrenToComponent<T extends ComponentType<any>> = ReactElement<ComponentProps<T>> | ReactElement<ComponentProps<T>>[] | Promise<Awaited<ReactElement<ComponentProps<T>>>> | Promise<Awaited<ReactElement<ComponentProps<T>>[]>>;
export function Row(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
Row.FirstSlot = FirstSlot;
Row.SecondSlot = SecondSlot;
function Slot(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.slot, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function FirstSlot(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.firstSlot, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Slot className={t1}>{children}</Slot>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function SecondSlot(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.secondSlot, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Slot className={t1}>{children}</Slot>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function Content(t0) {
  const $ = _c(3);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className) {
    t1 = <Row><Row.SecondSlot className={className}>{children}</Row.SecondSlot></Row>;
    $[0] = children;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function TitleBar(t0) {
  const $ = _c(7);
  const {
    children,
    icon: t1
  } = t0;
  const icon = t1 === undefined ? "heart" : t1;
  let t2;
  if ($[0] !== icon) {
    t2 = <Row.FirstSlot className={styles.iconSlot}><Icon className={styles.icon} name={icon} /></Row.FirstSlot>;
    $[0] = icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <Row.SecondSlot><div className={styles.titleBarContent}>{children}</div></Row.SecondSlot>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Row>{t2}{t3}</Row>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export default Object.assign(Banner, {
  TitleBar,
  Row,
  Content
});